import React, { useState, useEffect } from 'react';
import { 
  Inbox, Stethoscope, Pill, FlaskConical, HeartPulse, Send, 
  Search, Filter, CheckCircle, Clock, AlertTriangle, RefreshCw, Eye, RotateCw, X, Server
} from 'lucide-react';
import { AdminService } from '../../services/api';
import './LeadsManagement.css';

export default function LeadsManagement({ activeSubTab = 'all', showCustomAlert }) {
  const [subTab, setSubTab] = useState(activeSubTab);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);

  // Delivery Logs state
  const [deliveryLogs, setDeliveryLogs] = useState([]);
  const [deliverySearch, setDeliverySearch] = useState('');
  const [deliveryStateFilter, setDeliveryStateFilter] = useState('all');
  const [deliveryModeFilter, setDeliveryModeFilter] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  useEffect(() => {
    setSubTab(activeSubTab);
  }, [activeSubTab]);

  useEffect(() => {
    if (subTab === 'delivery') {
      fetchDeliveryLogs();
      // Ensure lead counters are also populated
      if (!stats.total_leads) {
        fetchLeadStatsOnly();
      }
    } else {
      fetchLeads();
    }
  }, [subTab, statusFilter]);

  const fetchLeadStatsOnly = async () => {
    try {
      const res = await AdminService.getLeads({ limit: 1 });
      if (res.status === 1 && res.stats) {
        setStats(res.stats);
      }
    } catch (_) {}
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      let service_type = 'all';
      if (subTab === 'doctor') service_type = 'doctor_consultation';
      else if (subTab === 'pharmacy') service_type = 'pharmacy_medicine';
      else if (subTab === 'lab') service_type = 'lab_test';
      else if (subTab === 'homecare') service_type = 'home_care';

      const response = await AdminService.getLeads({
        service_type,
        lead_status: statusFilter,
      });

      if (response.status === 1) {
        setLeads(response.data || []);
        if (response.stats) setStats(response.stats);
      } else {
        if (showCustomAlert) showCustomAlert(response.msg || 'Failed to load leads');
      }
    } catch (err) {
      console.error('Error fetching leads:', err);
      if (showCustomAlert) showCustomAlert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryLogs = async () => {
    setLoading(true);
    try {
      const response = await AdminService.getLeadDeliveryLogs();
      if (response.status === 1) {
        setDeliveryLogs(response.data || []);
      }
    } catch (err) {
      console.error('Error loading delivery logs:', err);
      if (showCustomAlert) showCustomAlert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRetryDelivery = async (logId) => {
    setRetryingId(logId);
    try {
      const res = await AdminService.retryLeadDelivery(logId);
      if (res.status === 1) {
        if (showCustomAlert) showCustomAlert('Lead transmission retry dispatched successfully');
        fetchDeliveryLogs();
      } else {
        if (showCustomAlert) showCustomAlert(res.msg || 'Failed to retry delivery');
      }
    } catch (err) {
      if (showCustomAlert) showCustomAlert(err.message);
    } finally {
      setRetryingId(null);
    }
  };

  const handleUpdateStatus = async (leadId, newStatus) => {
    try {
      const response = await AdminService.updateLeadStatus({
        id: leadId,
        lead_status: newStatus,
      });
      if (response.status === 1) {
        if (showCustomAlert) showCustomAlert('Lead updated successfully');
        fetchLeads();
        if (selectedLead && selectedLead.id === leadId) {
          setSelectedLead({ ...selectedLead, lead_status: newStatus });
        }
      }
    } catch (err) {
      if (showCustomAlert) showCustomAlert(err.message);
    }
  };

  const filteredLeads = leads.filter((l) => {
    const matchSearch = 
      (l.lead_number && l.lead_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (l.patient_name && l.patient_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (l.patient_phone && l.patient_phone.includes(searchTerm)) ||
      (l.patient_location && l.patient_location.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchSearch;
  });

  const filteredLogs = deliveryLogs.filter((log) => {
    const q = deliverySearch.toLowerCase();
    const matchSearch = 
      !q ||
      (log.lead_number && log.lead_number.toLowerCase().includes(q)) ||
      (log.vendor_name && log.vendor_name.toLowerCase().includes(q)) ||
      (log.endpoint_url && log.endpoint_url.toLowerCase().includes(q)) ||
      (log.delivery_mode && log.delivery_mode.toLowerCase().includes(q)) ||
      (String(log.id).includes(q));

    const matchState = deliveryStateFilter === 'all' || log.delivery_state === deliveryStateFilter;
    const matchMode = deliveryModeFilter === 'all' || log.delivery_mode === deliveryModeFilter;

    return matchSearch && matchState && matchMode;
  });

  return (
    <div className="leads-management-wrapper">
      {/* Header Subtabs */}
      <div className="leads-subtabs-nav">
        <button 
          className={`leads-tab-btn ${subTab === 'all' ? 'active' : ''}`}
          onClick={() => setSubTab('all')}
        >
          <Inbox size={16} /> All Leads ({stats.total_leads || leads.length || 0})
        </button>
        <button 
          className={`leads-tab-btn ${subTab === 'doctor' ? 'active' : ''}`}
          onClick={() => setSubTab('doctor')}
        >
          <Stethoscope size={16} /> Doctor Consultations ({stats.doctor_leads || 0})
        </button>
        <button 
          className={`leads-tab-btn ${subTab === 'pharmacy' ? 'active' : ''}`}
          onClick={() => setSubTab('pharmacy')}
        >
          <Pill size={16} /> Pharmacy Orders ({stats.pharmacy_leads || 0})
        </button>
        <button 
          className={`leads-tab-btn ${subTab === 'lab' ? 'active' : ''}`}
          onClick={() => setSubTab('lab')}
        >
          <FlaskConical size={16} /> Lab Diagnostics ({stats.lab_leads || 0})
        </button>
        <button 
          className={`leads-tab-btn ${subTab === 'homecare' ? 'active' : ''}`}
          onClick={() => setSubTab('homecare')}
        >
          <HeartPulse size={16} /> Home Care Leads ({stats.homecare_leads || 0})
        </button>
        <button 
          className={`leads-tab-btn ${subTab === 'delivery' ? 'active' : ''}`}
          onClick={() => setSubTab('delivery')}
        >
          <Send size={16} /> Delivery & Retry Logs ({deliveryLogs.length})
        </button>
      </div>

      {subTab !== 'delivery' ? (
        <>
          {/* Controls Bar for Leads */}
          <div className="leads-controls-bar">
            <div className="leads-search-box">
              <Search size={16} className="search-icon" />
              <input 
                type="text" 
                placeholder="Search leads by patient name, number, lead ID, location..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="leads-filters">
              <div className="filter-item">
                <Filter size={15} />
                <select 
                  value={statusFilter} 
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="new">New Ingestion</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="contacted">Contacted</option>
                  <option value="converted">Converted</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <button className="leads-refresh-btn" onClick={fetchLeads} title="Refresh">
                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
              </button>
            </div>
          </div>

          {/* Leads Table */}
          <div className="leads-table-card">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 0.5rem' }} />
                <p>Loading patient leads...</p>
              </div>
            ) : filteredLeads.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                <Inbox size={32} color="#94a3b8" style={{ margin: '0 auto 0.5rem' }} />
                <p><strong>No leads found matching your filter</strong></p>
              </div>
            ) : (
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Lead Number</th>
                    <th>Patient Details</th>
                    <th>Service Type</th>
                    <th>Requested Details</th>
                    <th>Assigned Vendor</th>
                    <th>Delivery Target</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => (
                    <tr key={lead.id}>
                      <td>
                        <strong className="lead-num-badge">#{lead.lead_number}</strong>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {new Date(lead.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td>
                        <strong>{lead.patient_name}</strong>
                        <div style={{ fontSize: '0.8rem', color: '#475569' }}>{lead.patient_phone}</div>
                        {lead.patient_location && (
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>📍 {lead.patient_location}</div>
                        )}
                      </td>
                      <td>
                        <span className={`service-tag tag-${lead.service_type}`}>
                          {lead.service_type === 'doctor_consultation' && 'Doctor Consult'}
                          {lead.service_type === 'pharmacy_medicine' && 'Pharmacy Medicine'}
                          {lead.service_type === 'lab_test' && 'Lab Diagnostic'}
                          {lead.service_type === 'home_care' && 'Home Care'}
                        </span>
                        {lead.service_subtype && (
                          <div style={{ fontSize: '0.75rem', color: '#475569', marginTop: '2px' }}>
                            {lead.service_subtype}
                          </div>
                        )}
                      </td>
                      <td>
                        <div><strong>Date:</strong> {lead.requested_date ? new Date(lead.requested_date).toLocaleDateString() : 'ASAP'}</div>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Time: {lead.requested_time || 'Standard Slot'}</div>
                      </td>
                      <td>
                        {lead.vendor_name ? (
                          <strong>{lead.vendor_name}</strong>
                        ) : (
                          <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Pending Auto-Route</span>
                        )}
                      </td>
                      <td>
                        <span className={`delivery-badge badge-${lead.delivery_mode}`}>
                          {lead.delivery_mode}
                        </span>
                      </td>
                      <td>
                        <select 
                          className={`status-select status-${lead.lead_status}`}
                          value={lead.lead_status}
                          onChange={(e) => handleUpdateStatus(lead.id, e.target.value)}
                        >
                          <option value="new">New</option>
                          <option value="acknowledged">Acknowledged</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td>
                        <button 
                          className="view-lead-btn"
                          onClick={() => setSelectedLead(lead)}
                        >
                          <Eye size={14} /> View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        /* ======================================================== */
        /* DELIVERY & RETRY LOGS TAB                                */
        /* ======================================================== */
        <div>
          {/* Controls Bar for Delivery Logs */}
          <div className="leads-controls-bar">
            <div className="leads-search-box">
              <Search size={16} className="search-icon" />
              <input 
                type="text" 
                placeholder="Search delivery logs by Lead Reference, Receiving Vendor, or Endpoint..." 
                value={deliverySearch}
                onChange={(e) => setDeliverySearch(e.target.value)}
              />
            </div>
            <div className="leads-filters">
              <div className="filter-item">
                <Filter size={15} />
                <select 
                  value={deliveryStateFilter} 
                  onChange={(e) => setDeliveryStateFilter(e.target.value)}
                >
                  <option value="all">All States</option>
                  <option value="success">Delivered (Success)</option>
                  <option value="failed">Failed / Retry Required</option>
                </select>
              </div>
              <div className="filter-item">
                <select 
                  value={deliveryModeFilter} 
                  onChange={(e) => setDeliveryModeFilter(e.target.value)}
                >
                  <option value="all">All Modes</option>
                  <option value="api_webhook">API Webhook</option>
                  <option value="cms_module">CMS Module</option>
                  <option value="lead_portal">Lead Portal</option>
                </select>
              </div>
              <button className="leads-refresh-btn" onClick={fetchDeliveryLogs} title="Refresh Logs">
                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
              </button>
            </div>
          </div>

          <div className="leads-table-card">
            <div className="delivery-logs-header" style={{ padding: '1.25rem', borderBottom: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Healthcare Vendor Lead Transmission & API Retry Logs</h3>
              <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                Real-time audit log tracking dispatches to External API Webhooks, Subscribed Digi Living CMS Modules, and Lead Portal Queues.
              </p>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 0.5rem' }} />
                <p>Loading delivery transmission logs...</p>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                <Send size={32} color="#94a3b8" style={{ margin: '0 auto 0.5rem' }} />
                <strong style={{ display: 'block', color: '#334155', marginBottom: '0.25rem' }}>No Delivery Logs Found</strong>
                <p style={{ margin: 0, fontSize: '0.85rem' }}>
                  All outgoing lead dispatches to vendor webhooks, portals, and CMS modules will appear here automatically.
                </p>
              </div>
            ) : (
              <table className="leads-table">
                <thead>
                  <tr>
                    <th>Log ID</th>
                    <th>Lead Reference</th>
                    <th>Receiving Healthcare Vendor</th>
                    <th>Delivery Mode</th>
                    <th>Destination / Endpoint</th>
                    <th>HTTP Status</th>
                    <th>Latency</th>
                    <th>Attempts</th>
                    <th>State</th>
                    <th>Timestamp</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log) => (
                    <tr key={log.id}>
                      <td><span style={{ fontFamily: 'monospace', color: '#64748b' }}>#{log.id}</span></td>
                      <td>
                        <strong>{log.lead_number || `Lead #${log.lead_id}`}</strong>
                        {log.service_type && (
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{log.service_type.replace('_', ' ')}</div>
                        )}
                      </td>
                      <td>
                        <strong>{log.vendor_name || `Vendor #${log.vendor_id}`}</strong>
                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Vendor ID #{log.vendor_id}</div>
                      </td>
                      <td>
                        <span className={`delivery-badge badge-${log.delivery_mode}`}>
                          {log.delivery_mode}
                        </span>
                      </td>
                      <td className="log-url-cell" style={{ maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.endpoint_url}>
                        <code>{log.endpoint_url || 'Internal Portal Queue'}</code>
                      </td>
                      <td>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          background: log.http_status >= 200 && log.http_status < 300 ? '#ecfdf5' : '#fef2f2',
                          color: log.http_status >= 200 && log.http_status < 300 ? '#047857' : '#dc2626'
                        }}>
                          {log.http_status || 200}
                        </span>
                      </td>
                      <td>{log.latency_ms ? `${log.latency_ms}ms` : 'Instant'}</td>
                      <td>
                        <span style={{ fontSize: '0.8rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                          Attempt #{log.attempt_number || 1}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          background: log.delivery_state === 'success' ? '#ecfdf5' : '#fef2f2',
                          color: log.delivery_state === 'success' ? '#047857' : '#dc2626'
                        }}>
                          {log.delivery_state === 'success' ? 'Delivered' : 'Failed'}
                        </span>
                      </td>
                      <td style={{ fontSize: '0.8rem', color: '#475569' }}>
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                          <button 
                            className="view-lead-btn" 
                            style={{ padding: '0.35rem 0.6rem' }}
                            onClick={() => setSelectedLog(log)}
                            title="View Transmission Payload"
                          >
                            <Eye size={13} /> View
                          </button>
                          <button 
                            className="view-lead-btn" 
                            style={{ 
                              padding: '0.35rem 0.6rem', 
                              background: log.delivery_state === 'failed' ? '#fee2e2' : '#ffffff',
                              color: log.delivery_state === 'failed' ? '#dc2626' : '#0284c7',
                              borderColor: log.delivery_state === 'failed' ? '#fca5a5' : '#0284c7' 
                            }}
                            disabled={retryingId === log.id}
                            onClick={() => handleRetryDelivery(log.id)}
                            title="Retry Transmission"
                          >
                            <RotateCw size={13} className={retryingId === log.id ? 'animate-spin' : ''} /> 
                            {log.delivery_state === 'failed' ? 'Retry' : 'Resend'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: VIEW LEAD DETAILS                                 */}
      {/* ======================================================== */}
      {selectedLead && (
        <div className="lead-modal-backdrop" onClick={() => setSelectedLead(null)}>
          <div className="lead-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Inbox size={18} color="#0284c7" />
                Lead #{selectedLead.lead_number}
              </h3>
              <button className="close-btn" onClick={() => setSelectedLead(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="lead-detail-grid">
                <div>
                  <label>Patient Inquirer:</label>
                  <p><strong>{selectedLead.patient_name}</strong></p>
                  <p>Phone: {selectedLead.patient_phone}</p>
                  <p>Email: {selectedLead.patient_email || 'N/A'}</p>
                  <p>Location: {selectedLead.patient_location || 'N/A'}</p>
                </div>
                <div>
                  <label>Service & Delivery Target:</label>
                  <p><strong>Service:</strong> {selectedLead.service_type}</p>
                  <p><strong>Target Provider:</strong> {selectedLead.vendor_name || 'Unassigned'}</p>
                  <p><strong>Requested Date:</strong> {selectedLead.requested_date || 'ASAP'}</p>
                  <p><strong>Delivery Mode:</strong> {selectedLead.delivery_mode}</p>
                </div>
              </div>

              {selectedLead.lead_payload && (
                <div className="lead-payload-box">
                  <label>Incoming Ingestion Payload:</label>
                  <pre>
                    {typeof selectedLead.lead_payload === 'string'
                      ? selectedLead.lead_payload
                      : JSON.stringify(selectedLead.lead_payload, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLead.patient_notes && (
                <div className="lead-notes-box">
                  <label>Patient Clinical / Requirement Notes:</label>
                  <p>{selectedLead.patient_notes}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setSelectedLead(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: VIEW DELIVERY & RETRY TRANSMISSION LOG            */}
      {/* ======================================================== */}
      {selectedLog && (
        <div className="lead-modal-backdrop" onClick={() => setSelectedLog(null)}>
          <div className="lead-modal-content" style={{ maxWidth: '680px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Server size={18} color="#0284c7" />
                Transmission Log #{selectedLog.id} — {selectedLog.lead_number || `Lead #${selectedLog.lead_id}`}
              </h3>
              <button className="close-btn" onClick={() => setSelectedLog(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="lead-detail-grid">
                <div>
                  <label>Receiving Healthcare Vendor:</label>
                  <p><strong>{selectedLog.vendor_name || `Vendor #${selectedLog.vendor_id}`}</strong></p>
                  <p>Delivery Channel: <strong>{selectedLog.delivery_mode}</strong></p>
                  <p>Attempt Count: <strong>#{selectedLog.attempt_number || 1}</strong></p>
                </div>
                <div>
                  <label>HTTP Response & Latency:</label>
                  <p>
                    HTTP Status: <strong style={{ color: selectedLog.http_status === 200 ? '#047857' : '#dc2626' }}>
                      {selectedLog.http_status || 200}
                    </strong>
                  </p>
                  <p>Latency: <strong>{selectedLog.latency_ms ? `${selectedLog.latency_ms}ms` : 'Instant'}</strong></p>
                  <p>Timestamp: <strong>{new Date(selectedLog.created_at).toLocaleString()}</strong></p>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.2rem' }}>
                  Target Endpoint URL / Queue:
                </label>
                <div style={{ background: '#f8fafc', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e1', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {selectedLog.endpoint_url || 'digiliving://lead-portal/internal-queue'}
                </div>
              </div>

              {selectedLog.error_message && (
                <div style={{ marginTop: '1rem', background: '#fef2f2', border: '1px solid #fecaca', padding: '0.75rem', borderRadius: '6px', color: '#991b1b', fontSize: '0.85rem' }}>
                  <strong>Failure Reason / Error Log:</strong>
                  <div style={{ marginTop: '0.25rem' }}>{selectedLog.error_message}</div>
                </div>
              )}

              {selectedLog.request_payload && (
                <div className="lead-payload-box">
                  <label>Outbound Request Payload Sent:</label>
                  <pre>
                    {typeof selectedLog.request_payload === 'string'
                      ? selectedLog.request_payload
                      : JSON.stringify(selectedLog.request_payload, null, 2)}
                  </pre>
                </div>
              )}

              {selectedLog.response_payload && (
                <div className="lead-payload-box" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                  <label style={{ color: '#166534' }}>Inbound Response Received from Vendor:</label>
                  <pre style={{ color: '#14532d' }}>
                    {typeof selectedLog.response_payload === 'string'
                      ? selectedLog.response_payload
                      : JSON.stringify(selectedLog.response_payload, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button 
                className="view-lead-btn" 
                style={{ background: '#0284c7', color: '#fff' }}
                onClick={() => {
                  handleRetryDelivery(selectedLog.id);
                  setSelectedLog(null);
                }}
              >
                <RotateCw size={14} /> Re-dispatch Transmission
              </button>
              <button className="btn-secondary" onClick={() => setSelectedLog(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
